import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, ChevronDown, X, Eye } from 'lucide-react';
import { products, productCategories, type Product } from '../data/products';

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const productId = searchParams.get('id');

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  useEffect(() => {
    if (productId) {
      const product = products.find((p) => p.id === productId);
      if (product) {
        setSelectedProduct(product);
      }
    }
  }, [productId]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'all') return products;
    return products.filter((p) =>
      (p.categories ?? [p.category]).includes(selectedCategory)
    );
  }, [selectedCategory]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
    setIsFilterOpen(false);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    searchParams.delete('id');
    setSearchParams(searchParams);
  };

  return (
    <main className="pt-20">
      <section className="py-16 md:py-24 bg-gradient-to-br from-deep-lake to-deep-lake-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif-zh font-bold text-white mb-6">
            产品中心
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            匠心之作，满足不同垂钓场景需求
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-300"
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">筛选</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isFilterOpen ? 'rotate-180' : ''}`} />
              </button>
              <span className="text-sm text-gray-500">
                共 {filteredProducts.length} 款产品
              </span>
            </div>
          </div>

          {isFilterOpen && (
            <div className="mb-8 p-4 bg-gray-50 rounded-xl animate-fade-in">
              <div className="flex flex-wrap gap-2">
                {productCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-deep-lake text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => {
                  setSelectedProduct(product);
                  searchParams.set('id', product.id);
                  setSearchParams(searchParams);
                }}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer border border-gray-100"
              >
                <div className="aspect-square overflow-hidden relative bg-gray-50">
                {product.videoUrl ? (
                    <>
                      <img
                        src={product.thumbnail || product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-8 h-8 text-deep-lake" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>

                    </>
                  ) : (
                    <>
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                          <Eye className="w-6 h-6 text-deep-lake" />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-serif-zh font-bold text-gray-900 mb-2 group-hover:text-deep-lake transition-colors duration-300">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-dawn-orange">
                      {product.price}
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      {product.specs.material}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={closeModal}>
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-serif-zh font-bold text-deep-lake">
                {selectedProduct.name}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-300"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              {selectedProduct.videoUrl ? (
                <div className="p-6">
                  <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                  <video
                      src={selectedProduct.videoUrl}
                      title={selectedProduct.name}
                      className="w-full h-full"
                      controls
                      preload="metadata"
                      playsInline
                    >
                      您的浏览器不支持视频播放，请升级浏览器或下载视频查看。
                    </video>
                  </div>
                  <div className="mt-6">
                    <span className="inline-block px-3 py-1 bg-dawn-orange/10 text-dawn-orange text-sm font-medium rounded-full mb-3">
                      {selectedProduct.series}
                    </span>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedProduct.description}
                    </p>
                  </div>
                </div>
              ) : selectedProduct.detailImages && selectedProduct.detailImages.length > 0 ? (
                <div className="flex flex-col">
                  {selectedProduct.detailImages.map((imgSrc, index) => (
                    <img
                      key={index}
                      src={imgSrc}
                      alt={`${selectedProduct.name} - 详情${index + 1}`}
                      className="w-full"
                    />
                  ))}
                </div>
              ) : (
                <div className="p-6">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="w-full rounded-xl"
                  />
                  <div className="mt-6">
                    <span className="inline-block px-3 py-1 bg-dawn-orange/10 text-dawn-orange text-sm font-medium rounded-full mb-3">
                      {selectedProduct.series}
                    </span>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedProduct.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
